/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";

import {betterMemo} from "js/common/utils/more-memo";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import LeaderboardAvatar from "js/dashboards/components/leaderboard/leaderboard-avatar";
import {bronze, gold, silver} from "js/common/cube19-colors";

export const leaderBoardRankColors = {
  1: gold,
  2: silver,
  3: bronze
};

const rowStyle = (theme, isHighlighted, highlightFollowsHover) => css`
  display: flex;
  align-items: center;
  padding: 8px;
  margin: 0;
  border-bottom: ${!isHighlighted ? `1px solid ${theme.palette.border.lightest}` : "none"};
  cursor: pointer;
  position: relative;
  border-radius: ${isHighlighted ? "5px" : "0"};
  color: ${isHighlighted ? theme.palette.leaderboard.highlightText : theme.palette.text.main};
  background-color: ${isHighlighted ? theme.palette.leaderboard.highlight : "transparent"};

  ${!highlightFollowsHover && `&:hover {
    background-color: ${theme.palette.background.paper};
    cursor: pointer;
    color: ${theme.palette.text.main};
  }`}
`;

const nameStyle = {
  fontSize: 14,
  lineHeight: 1.2,
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflowWrap: "break-word"
};

const compactNameStyle = {...nameStyle, fontSize: 13};

const RankBadge = ({theme, rank, isCompact}) => {
  const backgroundColor = leaderBoardRankColors[rank] ?? theme.palette.background.card;
  const isTopThree = rank <= 3;
  return <div
      style={{
        width: 20,
        height: 20,
        flexShrink: 0,
        borderRadius: "7px",
        transform: "rotate(45deg)",
        backgroundColor,
        color: isTopThree ? "white" : theme.palette.text.main,
        border: isTopThree ? "none" : `1px solid ${theme.palette.border.light}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: isCompact ? 10 : 15,
        fontSize: 12,
        fontWeight: "bold"
      }}>
    <div style={{transform: "rotate(-45deg)"}}>{rank}</div>
  </div>;
};

const LeaderboardRow = betterMemo({displayName: "LeaderboardRow"}, ({
  index,
  rank,
  user,
  value,
  onClick,
  onHover,
  isHighlighted,
  sortField,
  percentOfTarget,
  isCompact = false
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
      <div
          css={rowStyle(theme, isHighlighted, !!onHover)}
          data-test-id="leaderboard-row"
          data-highlighted={isHighlighted}
          data-compact={isCompact}
          onMouseEnter={onHover ? () => onHover(index) : undefined}
          onClick={() => onClick(user.get("id"))}>

        <RankBadge theme={theme} rank={rank} isCompact={isCompact} />

        {!isCompact && <LeaderboardAvatar rank={rank} user={user} />}

        {/* The name gives way when the row runs short of width, so everything else keeps its shape. */}
        <div style={{flex: 1, minWidth: 0}}>
          <div title={user.get("fullName")} style={isCompact ? compactNameStyle : nameStyle}>
            {user.get("fullName")}
          </div>
        </div>

        <div style={{display: "flex", flexShrink: 0, marginLeft: 8, whiteSpace: "nowrap"}}>
          <div style={{fontWeight: "bold"}}>{sortField === "VALUE" ? value : percentOfTarget}</div>
        </div>
      </div>
  );
});

export default LeaderboardRow;
